import { GlobalMessages } from '@shared/localization/messages';
import { useMemo } from 'react';
import { type Control, useWatch } from 'react-hook-form';

import type { CustomDropdownOption } from '@/components/atoms/CustomDropdown';
import { DropdownFormField } from '@/components/atoms/DropdownFormField';
import { EstimationUnitName } from '@/components/atoms/EstimationUnitName';
import { FormattedNumber } from '@/components/atoms/FormattedNumber';
import { InlineEditableText } from '@/components/atoms/InlineEditableText';
import { SanitizedHtml } from '@/components/atoms/SanitizedHtml';
import { TextFormField } from '@/components/atoms/TextFormField';
import { UserAutocompleteFormField } from '@/components/atoms/UserAutocompleteFormField';
import { WysiwygFormTextField } from '@/components/atoms/WysiwygFormTextField';
import { UserFullName } from '@/components/UserFullName';
import { entityFieldSettings } from '@/constants/entityFieldSettings';
import { useMessages } from '@/hooks/messages';
import { sortExpressions } from '@/utils/sorting';

import type {
  EstimationItemDetail,
  EstimationItemMethodologyChangeCallback,
  EstimationItemsEditFormModel,
  MethodologyData,
} from '../model';
import { estimationItemDetailEditationMessageDefinitions } from './messages';

interface TableDisplayModel {
  methodologies: CustomDropdownOption[];
  estimationItem: EstimationItemDetail;
}

interface EstimationItemEditationProps {
  control: Control<EstimationItemsEditFormModel>;
  methodologies: MethodologyData[];
  estimationItem: EstimationItemDetail;
  isReadOnly: boolean;
  subItemsRealTimeSum: number | undefined; // undefined when there are no sub-items
  onMethodologyChange: EstimationItemMethodologyChangeCallback;
  /**
   * When provided, the Phasenname field becomes an inline-edit input that saves immediately
   * via the lightweight rename endpoint (EWM-589). Independent of "Schätzung aktualisieren".
   */
  onInlineRename?: (newName: string) => Promise<void>;
}

export const EstimationItemEditation: FC<EstimationItemEditationProps> = ({
  control,
  methodologies,
  estimationItem,
  isReadOnly,
  subItemsRealTimeSum,
  onMethodologyChange,
  onInlineRename,
}) => {
  const globalMessages = useMessages(GlobalMessages);
  const messages = useMessages(estimationItemDetailEditationMessageDefinitions);
  const formModel = useWatch({ control });

  if (!formModel.mainEstimationItem) {
    throw new Error('mainEstimationItem cannot be undefined');
  }

  const tableRecords = useMemo<TableDisplayModel[]>(
    () => [
      {
        methodologies: methodologies.sort(sortExpressions.byOrder).map((x) => ({
          label: x.name,
          value: x.id,
        })),
        estimationItem: estimationItem,
      },
    ],
    [methodologies, estimationItem],
  );

  const hasEstimationModelItemParameters = estimationItem.params.some(
    (x) => x.estimationModelItemParameterId,
  );
  // Not every item's methodology is in this collection: it holds the ones the estimation model
  // links, and an item can reference another (EWM-605 - a converted estimation in TEST did exactly
  // that on all 114 items and killed the Estimation tab through the same assertion).
  const methodologyName = estimationItem.methodologyId
    ? methodologies.find((x) => x.id === estimationItem.methodologyId)?.name
    : undefined;

  return (
    <div className='space-y-5'>
      <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
        <div>
          <label htmlFor='mainEstimationItem.name'>{messages.phaseName()}</label>
          {isReadOnly ? (
            formModel.mainEstimationItem.name
          ) : onInlineRename ? (
            <InlineEditableText
              value={formModel.mainEstimationItem.name ?? ''}
              onSubmit={onInlineRename}
              maxLength={entityFieldSettings.estimationItemEntity.nameMaxLength}
              ariaLabel={messages.phaseName()}
            />
          ) : (
            <TextFormField
              control={control}
              fieldPath='mainEstimationItem.name'
              rules={{
                required: true,
                maxLength: entityFieldSettings.estimationItemEntity.nameMaxLength,
              }}
            />
          )}
        </div>
        <div>
          <label htmlFor='mainEstimationItem.phaseLeadId'>{messages.phaseLead()}</label>
          {isReadOnly ? (
            <UserFullName userId={estimationItem.phaseLeadId} />
          ) : (
            <UserAutocompleteFormField
              control={control}
              fieldPath='mainEstimationItem.phaseLeadId'
            />
          )}
        </div>
      </div>
      <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
        <div>
          <label htmlFor='mainEstimationItem.description'>{messages.description()}</label>
          {isReadOnly ? (
            <SanitizedHtml htmlString={formModel.mainEstimationItem.description} />
          ) : (
            <WysiwygFormTextField
              control={control}
              fieldPath='mainEstimationItem.description'
              rules={{ maxLength: entityFieldSettings.estimationItemEntity.descriptionMaxLength }}
            />
          )}
        </div>
        <div className='flex flex-col gap-4'>
          <div>
            <label htmlFor='mainEstimationItem.methodologyId'>{messages.methodology()}</label>
            {hasEstimationModelItemParameters ? (
              globalMessages.notAvailable()
            ) : isReadOnly || !!estimationItem.parentId ? (
              methodologyName
            ) : (
              <DropdownFormField
                control={control}
                fieldPath='mainEstimationItem.methodologyId'
                options={tableRecords[0].methodologies}
                onChange={(methodologyId) => onMethodologyChange(estimationItem.id, methodologyId)}
              />
            )}
          </div>
          <div>
            <label htmlFor='estimate'>
              {messages.estimate()} Δ (
              <EstimationUnitName estimationUnit={estimationItem.unit} />)
            </label>
            {subItemsRealTimeSum === undefined ? (
              <FormattedNumber value={estimationItem.calculatedResult ?? 0} />
            ) : (
              <div
                className={
                  (estimationItem.calculatedResult ?? 0) - subItemsRealTimeSum !== 0
                    ? 'text-amber-500'
                    : ''
                }
              >
                <FormattedNumber
                  value={(estimationItem.calculatedResult ?? 0) - subItemsRealTimeSum}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
